import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Grid, useTheme } from '@mui/material';
import { Autocomplete, Card, DatePicker, ValidatorForm } from 'components'; // Assuming DatePicker is a custom component
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import DashboardServices from 'services/DashboardServices';
import moment from 'moment';
import { dateParse } from 'Utils';

const IncidentsByDailyWeekly = ({ filters }) => {
    const theme = useTheme();
    const [params, setParams] = useState({
        period: "daily",
        from_date: moment().subtract(15, 'days').toISOString(),
        to_date: moment().toISOString()
    });
    const [data, setData] = useState([
    ]);

    const getData = useCallback(async () => {
        const response = await DashboardServices.getIncidetReportByDailyWeekly({ params, ...filters });
        if (response.success) {
            setData(response?.data || []);
        }
    }, [params, filters]);

    useEffect(() => {
        getData();
    }, [getData]);

    const handlePeriodChange = (event) => {
        const period = event.target.value;
        let from_date, to_date = moment().toISOString();

        if (period === 'daily') {
            from_date = moment().subtract(15, 'days').toISOString();
        } else if (period === 'weekly') {
            from_date = moment().subtract(15, 'weeks').toISOString();
        } else if (period === 'monthly') {
            from_date = moment().subtract(12, 'months').toISOString();
        }

        setParams({ period, from_date, to_date });
    };

    const handleDateChange = (field, date) => {
        setParams({ ...params, [field]: date });
    };

    const fillMissingDates = (data, period) => {
        const filledData = [];
        let current = moment(params.from_date);
        const end = moment(params.to_date);

        while (current <= end) {
            const formattedDate = current.toISOString();
            const existingData = data.find(d => moment(d.period).isSame(current, period === 'daily' ? 'day' : period === 'weekly' ? 'week' : 'month'));
            filledData.push({
                period: formattedDate,
                count: existingData ? existingData.count : 0
            });
            current.add(1, period === 'daily' ? 'days' : period === 'weekly' ? 'weeks' : 'months');
        }

        return filledData;
    };

    const filledData = fillMissingDates(data, params.period);

    const containerStyle = {
        borderRadius: '8px',
    };

    return (
        <Card className="p-4" style={containerStyle}>
            <Typography variant='h6'>Incidents By Period</Typography>
            <ValidatorForm className="mt-3">
                <Grid container spacing={2} columns={10}>
                    <Grid item size={{ lg: 2, md: 2, sm: 12, xs: 12 }}>
                        <Autocomplete
                            name="period"
                            label="Type"
                            value={[{ value: 'daily', lable: 'Daily' }, { value: 'weekly', lable: 'Weekly' }, { value: 'monthly', lable: 'Monthly' }].find((x) => x.value === params.period) || null}
                            onChange={(e, value) => {
                                if (value !== null) {
                                    handlePeriodChange({ target: { value: value.value } });
                                }
                            }}
                            options={[{ value: 'daily', lable: 'Daily' }, { value: 'weekly', lable: 'Weekly' }, { value: 'monthly', lable: 'Monthly' }]}
                            getOptionLabel={option => option?.lable || ''}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        />
                    </Grid>
                    <Grid item size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
                        <DatePicker
                            label="From Date"
                            value={new Date(params.from_date)}
                            onChange={(date) => handleDateChange('from_date', date)}
                        />
                    </Grid>
                    <Grid item size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
                        <DatePicker
                            label="To Date"
                            value={new Date(params.to_date)}
                            onChange={(date) => handleDateChange('to_date', date)}
                        />
                    </Grid>
                </Grid>
            </ValidatorForm>

            <ResponsiveContainer className="mt-5" width="100%" height={325}>
                <BarChart data={filledData}>
                    <defs>
                        <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#4cc4c3" stopOpacity={0.5} />
                            <stop offset="95%" stopColor="#4cc4c3" stopOpacity={1} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" tickFormatter={dateParse} stroke={theme.palette.text.primary} />
                    <YAxis allowDecimals={false} domain={[0, 'dataMax + 1']}  stroke={theme.palette.text.primary} />
                    <Tooltip
                        formatter={(value) => value}
                        labelFormatter={(label) => dateParse(label)}
                        labelStyle={{ color: '#363636' }}
                        contentStyle={{ color: '#363636' }}
                    />
                    <Bar dataKey="count" fill="url(#colorUv)" />
                </BarChart>
            </ResponsiveContainer>
        </Card>
    );
};

export default IncidentsByDailyWeekly;